import { Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisLabel,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  SmoothedXLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { format } from "date-fns";
import { cloneDeep, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { otherValues } from "@/settings/constants";
import { chartFontSettings, chartInitialSettings, dateAxisDateFormats } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

export default class StreamChart extends BaseChart {
  interval!: time.ITimeInterval;
  seriesNames!: string[];

  declare chart: XYChart;
  declare renderers: {
    x: AxisRendererX;
    y: AxisRendererY;
  };

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      this.chart.series.clear();
    }

    this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;

    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
        maxTooltipDistance: -1,
      })
    );

    this.createRenderers();
    this.createAxes();
    this.createCursor();

    const paletteLength = this.palette.length;
    this.seriesNames.forEach((name, index) => {
      return this.createSeries(name, index % paletteLength);
    });

    const data = [...this.data];

    // Prepare data for the river-stacked series
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      let sum = 0;

      // Calculate open and close values
      this.chart.series.each(function (series) {
        const field = series.get("valueField");
        const val = Number(row[field as string]);
        row[`${field}_low`] = sum;
        row[`${field}_hi`] = sum + val;
        sum += val;
      });

      // Adjust values so they are centered
      const offset = sum / 2;
      this.chart.series.each(function (series) {
        const field = series.get("valueField");
        (row[`${field}_low`] as number) -= offset;
        (row[`${field}_hi`] as number) -= offset;
      });

      this.chart.series.each(function (series) {
        series.data.setAll(data);
      });
    }

    this.showLegend(this.seriesNames, this.chart.series.values as LegendData);
  }

  protected mapData(data: ChartDataRecord[]) {
    let mappedData: ChartDataRecord[] = [];
    const groupedData: Record<string, ChartDataRecord> = {};
    const seriesNames: Set<string> = new Set();

    data = orderBy(data, this.settings.decompositionMetric, "asc");

    let preparedData = cloneDeep(data);

    if (
      this.module === PLAN_MODULE &&
      !this.settings?.useExploreData &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const displayMetric = this.settings.displayMetric;
      const decompositionMetric = this.settings.decompositionMetricTop;

      const groupedPreparedDataMap = preparedData.reduce<Record<string, number>>((acc, item) => {
        const recordItem = item as ChartDataRecord;
        const groupKey = recordItem[decompositionMetric] as string;
        const currentValue = Number(recordItem[displayMetric] ?? 0);

        acc[groupKey] = (acc[groupKey] || 0) + currentValue;

        return acc;
      }, {});
      const groupedPreparedData = Object.entries(groupedPreparedDataMap).map(([key, total]) => ({ key, total }));
      const sortedGroupedPreparedData = groupedPreparedData.sort((a, b) => b.total - a.total);
      const selectedGroupedPreparedData = sortedGroupedPreparedData.slice(0, Number(this.settings.showItems));
      const selectedDecompositionDimensions = selectedGroupedPreparedData.map((item) => item.key);

      preparedData = preparedData.filter((item) =>
        selectedDecompositionDimensions.includes((item as ChartDataRecord)[decompositionMetric] as string)
      );
    }

    preparedData.forEach((el) => {
      const dateKey = String(el[this.settings.decompositionMetric]);
      const metricKey = String(
        otherValues.includes(String(el[this.settings.decompositionMetricTop]))
          ? Lang.other
          : el[this.settings.decompositionMetricTop]
      );

      if (!groupedData[dateKey]) {
        groupedData[dateKey] = { [this.settings.decompositionMetric]: dateKey };
      }

      groupedData[dateKey][metricKey] = (el[this.settings.displayMetric] as number) ?? 0;
      seriesNames.add(metricKey);
    });

    mappedData = Object.values(groupedData);

    mappedData.forEach((row) => {
      seriesNames.forEach((seriesName) => {
        if (row[seriesName] === undefined) {
          row[seriesName] = 0;
        }
      });
    });

    this.seriesNames = Array.from(seriesNames);

    return this.showItems(mappedData, this.settings.decompositionMetricTop, this.settings.displayMetric);
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, { minGridDistance: 60 });
    const y = AxisRendererY.new(this.root, {});

    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    y.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.renderers = { x, y };
  }

  protected createAxes() {
    const x = this.chart.xAxes.push(
      CategoryAxis.new(this.root, {
        renderer: this.renderers.x,
        categoryField: this.settings.decompositionMetric,
        startLocation: 0.5,
        endLocation: 0.5,
      })
    );

    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          paddingBottom: 0,
          centerX: p50,
        })
      );
    }

    const y = this.chart.yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.y,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
      })
    );

    if (this.settings.yAxisTitle) {
      y.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    const lastDataPoint = this.data[this.data.length - 1];
    if (lastDataPoint) {
      const lastDate = lastDataPoint[this.settings.decompositionMetric];
      if (!this.data.find((d) => d[this.settings.decompositionMetric] === lastDate)) {
        this.data.push({ ...lastDataPoint });
      }
    }

    x.data.setAll(this.data);

    x.get("renderer").labels.template.adapters.add("text", (text, target: AxisLabel) => {
      if (target.dataItem && target.dataItem.dataContext) {
        const dataItem = target.dataItem.dataContext as ChartDataRecord;
        const date = stringOrNumberToDate(dataItem[this.settings.decompositionMetric] as string | number);
        return format(
          date,
          (dateAxisDateFormats as Record<string, string>)[this.settings.decompositionMetric as string]
        );
      } else {
        return text;
      }
    });

    this.axes = { x, y };
  }

  protected createCursor() {
    const cursor = this.chart.set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.axes.x,
      })
    );

    cursor.lineX.set("visible", true);
    cursor.lineY.set("visible", true);
  }

  protected createSeries(name: string, index: number) {
    const series = this.chart.series.push(
      SmoothedXLineSeries.new(this.root, {
        name,
        xAxis: this.axes.x,
        yAxis: this.axes.y,
        valueField: name,
        valueYField: `${name}_hi`,
        openValueYField: `${name}_low`,
        categoryXField: this.settings.decompositionMetric,
        fill: this.getSeriesColor(index),
      })
    );

    this.createTooltip(series, () => {
      const param: Record<string, unknown>[] = [];
      const values = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;

      if (values) {
        param.push({ key: this.settings.decompositionMetricTop, value: name });
        param.push({ key: this.settings.decompositionMetric, value: values[this.settings.decompositionMetric] });
        param.push({ key: this.settings.displayMetric, value: values[name] });
      }

      return param;
    });

    series.strokes.template.setAll({
      forceHidden: true,
    });

    series.fills.template.setAll({
      visible: true,
      fillOpacity: 1,
    });
  }
}
